import React from 'react';
import { useFormikContext } from 'formik';
import * as Yup from 'yup';

import CheckboxFormik from '~/components/CheckboxFormik';
import Grid from '~/components/core/Atomic/Grid/Grid';
import MenuItem from '~/components/core/Atomic/MenuItem';
import { InfoIcon } from '~/components/icons';
import type { ClosingReasonClaim } from '~/server_shared/swagger/data-contracts';

import { LoadingSwitch } from '../core';
import TooltipIcon from '../core/TooltipIcon';
import TextFieldFormik from '../TextFieldFormik';
import useDataFetcher from '../useDataFetcher';

/** The closing reasons an organization offers, keyed by `closing_reason_key`. */
export type ClaimClosingReasonDict = Record<string, ClosingReasonClaim>;

/** The values the fields below collect, and `POST /claims/{claim_id}/close` accepts. */
export interface ClaimClosingReasonValues {
  claim_reason: string;
  claim_extra: string;
  should_dismiss_all_claim_notifications: boolean;
}

/**
 * The configurable closing-reason form, shared by every surface that collects one: the reason
 * select, the note (enabled/required per the chosen reason's config) and the dismiss-notifications
 * checkbox. Must render inside a Formik whose values were seeded with
 * CLAIM_CLOSING_REASON_INITIAL_VALUES and validated with getClaimClosingReasonValidationSchema.
 */
export const CLAIM_CLOSING_REASON_INITIAL_VALUES: ClaimClosingReasonValues = {
  claim_reason: '',
  claim_extra: '',
  should_dismiss_all_claim_notifications: false,
};

export const getClaimClosingReasonValidationSchema = (reasonDict: ClaimClosingReasonDict) => ({
  claim_reason: Yup.string().required('Required'),
  claim_extra: Yup.string().when('claim_reason', {
    is: (val: string) => !!(val && reasonDict?.[val].is_note_required),
    then: Yup.string().required('Required'),
  }),
  should_dismiss_all_claim_notifications: Yup.boolean(),
});

// `closing_reason_key` is nullable=False on the closing-reason config table; the generated
// contract types it optional only because flask-restx marshals every field as optional. Narrowing
// it here keeps the dict's key type honest without asserting over the contract.
const hasReasonKey = (reason: ClosingReasonClaim): reason is ClosingReasonClaim & { closing_reason_key: string } =>
  !!reason.closing_reason_key;

export const useClaimClosingReasons = (claim: {
  id: number;
}): { isLoading: boolean; isError: boolean; reasonDict: ClaimClosingReasonDict } => {
  const {
    isLoading,
    isError,
    data: closingReasons,
  } = useDataFetcher<ClosingReasonClaim[]>(`/api/v1/claims/${claim.id}/closing_reasons`);
  const reasonDict = Object.fromEntries(
    (closingReasons || []).filter(hasReasonKey).map((reason) => [reason.closing_reason_key, reason])
  );
  return { isLoading, isError, reasonDict };
};

interface ClaimClosingReasonFieldsProps {
  reasonDict: ClaimClosingReasonDict;
  isLoading: boolean;
  isError: boolean;
}

export const ClaimClosingReasonFields: React.FC<ClaimClosingReasonFieldsProps> = ({
  reasonDict,
  isLoading,
  isError,
}) => {
  const { values } = useFormikContext<ClaimClosingReasonValues>();

  const isNoteEnabled = values.claim_reason && reasonDict?.[values.claim_reason]?.is_note_enabled;

  return (
    <LoadingSwitch isLoading={isLoading} isError={isError}>
      <Grid container spacing={1}>
        <Grid item xs={6}>
          <TextFieldFormik id="claim_reason" select label="Reason" className="my-4" fullWidth>
            {/* @ts-expect-error - TextFieldFormik not yet typed and its type inferrence is flawed */}
            {Object.keys(reasonDict || {})
              .sort((a, b) => (reasonDict?.[a]?.display_name || '').localeCompare(reasonDict?.[b]?.display_name || ''))
              .map((reason) => (
                <MenuItem key={reason} value={reason}>
                  {reasonDict?.[reason]?.display_name}
                </MenuItem>
              ))}
          </TextFieldFormik>
        </Grid>
        <Grid item xs={6}>
          <TextFieldFormik id="claim_extra" label="Note" disabled={!isNoteEnabled} fullWidth className="my-4" />
        </Grid>
        <Grid item xs={12}>
          <CheckboxFormik
            id="should_dismiss_all_claim_notifications"
            label={
              <div className="inline-flex items-baseline">
                Dismiss all claim notifications
                <TooltipIcon
                  placement="right"
                  arrow
                  title="Only notifications you are authorized to dismiss will be affected"
                >
                  {/* InfoIcon (JS) infers its optional iconColor as required; it stays omitted so
                      SvgIcon applies its default colour, as before the extraction. */}
                  {/* @ts-expect-error - InfoIcon is not yet typed and its type inferrence is flawed */}
                  <InfoIcon className="ml-4" size={18} />
                </TooltipIcon>
              </div>
            }
          />
        </Grid>
      </Grid>
    </LoadingSwitch>
  );
};
